import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import Main from 'layouts/Main';
import useProductData from '../../../Hooks/useProductData';
import useTableData from 'Hooks/useTableData';
import useCategoryDetails from 'Hooks/useCategoryDetails';
import useCategories from 'Hooks/useCategories';
import useQuery from 'Hooks/useQueryParam';

import { ProductsTable, FiltersWithDropdown } from './components';
import { SearchFilter } from 'types/item';
import { Filters, FilterGroup } from '@akyga/shared-ui/UberFilter';
import { ColumnConfig } from '@akyga/shared-ui';
import InlineError from 'components/InlineError';

const EMPTY_FILTERS: SearchFilter[] = [];

const Simple = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParamsGetter = useQuery();
  const [searchInput, setSearchInput] = useState(
    () => queryParamsGetter.get('q') ?? '',
  );

  const partNumber = queryParamsGetter.get('partNumber');
  const itemcode = params.itemcode;
  const filtersParam = queryParamsGetter.get('filters');
  const initialFilters = useMemo<SearchFilter[]>(() => {
    if (!filtersParam) return EMPTY_FILTERS;
    try {
      const parsed = JSON.parse(filtersParam);
      return Array.isArray(parsed) ? parsed : EMPTY_FILTERS;
    } catch (error) {
      return EMPTY_FILTERS;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersParam]);

  // Seed appliedFilters from the URL synchronously so the URL-sync effect
  // below never sees a transient empty state and strips a shared "filters"
  // param before FiltersWithDropdown (which only mounts once table/category
  // data has loaded) gets a chance to apply it.
  const [appliedFilters, setAppliedFilters] = useState<SearchFilter[]>(
    () => initialFilters,
  );

  // Keep the URL in sync with search text and applied filters so the
  // current results are shareable via a copy-pasted link.
  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    if (searchInput) {
      nextParams.set('q', searchInput);
    } else {
      nextParams.delete('q');
    }
    if (appliedFilters && appliedFilters.length > 0) {
      nextParams.set('filters', JSON.stringify(appliedFilters));
    } else {
      nextParams.delete('filters');
    }
    const nextSearch = nextParams.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      navigate(
        { pathname: location.pathname, search: nextSearch },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, appliedFilters]);

  const { data: filteredData } = useProductData(
    params.name,
    partNumber,
    appliedFilters,
    searchInput,
    itemcode,
  );
  const { data: allCategoryData } = useProductData(
    params.name,
    null,
    EMPTY_FILTERS,
    '',
    null,
  );
  const {
    data: tableData = [],
    error: tableError,
    execute: refetchTableData,
  } = useTableData(params.name);
  const { data: categories = [] } = useCategories();
  const { data: groupsRaw = [] } = useCategoryDetails(params.name as string);
  const groups = groupsRaw as
    | { columns?: ColumnConfig[]; filters?: { filterGroups: FilterGroup[] } }
    | [];

  const hasTableData =
    !!tableData && !Array.isArray(tableData) && 'groupDetails' in tableData;

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    value: string,
  ) => {
    setSearchInput(value);
  };

  const categoriesLoaded = categories && categories.length > 0;

  /*
      All right, that's one terrible workaround.
      I'd suggest asing Slawek to add basicUnit to controls information
      When that happens, simply ditch this function and enchanced filters and replace with previous filters :)
  */
  const enhanceFiltersWithBasicUnit = (
    groupsData: typeof groups,
  ): Filters | null => {
    const g = groupsData as {
      columns?: ColumnConfig[];
      filters?: { filterGroups: FilterGroup[] };
    };
    if (!g || !g.filters || !g.filters.filterGroups) {
      return null;
    }

    const { columns, filters } = g;

    const enhancedFilterGroups = filters.filterGroups.map((filterGroup) => {
      const enhancedControls = filterGroup.controls.map((control) => {
        const matchingColumn = columns?.find(
          (column) => column.name === control.column,
        );

        if (matchingColumn) {
          return {
            ...control,
            basicUnit: matchingColumn.basicUnit,
          };
        }

        return control;
      });

      return {
        ...filterGroup,
        controls: enhancedControls,
      };
    });

    return {
      ...filters,
      filterGroups: enhancedFilterGroups,
    };
  };

  const enhancedFilters = enhanceFiltersWithBasicUnit(groups);

  const tableDataObj = tableData as {
    groupDetails?: { imageSrc?: string };
    siblings?: { name?: string }[];
  };

  return (
    <Main>
      <Box bgcolor={'alternate.main'}>
        <Container>
          {tableError && !hasTableData && (
            <InlineError onRetry={() => refetchTableData()} />
          )}
          {hasTableData && categoriesLoaded && enhancedFilters && (
            <FiltersWithDropdown
              name={params.name as string}
              handleSearchChange={handleSearchChange}
              searchInput={searchInput}
              splashImage={tableDataObj.groupDetails?.imageSrc}
              siblings={tableDataObj.siblings ?? []}
              categories={categories}
              allCategoryData={allCategoryData}
              filters={enhancedFilters} // Workaround
              handleFilterApply={setAppliedFilters}
              partNumber={partNumber}
              itemcode={itemcode}
              initialFilters={initialFilters}
            />
          )}
          <div style={{ overflowX: 'auto' }}>
            {hasTableData && (
              <ProductsTable
                columns={(groups as { columns?: ColumnConfig[] })?.columns}
                filteredData={filteredData}
                itemcode={itemcode}
              />
            )}
          </div>
        </Container>
      </Box>
    </Main>
  );
};

export default Simple;
